import Link from 'found/Link';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';

import { AdminTablePlaceholderRows } from 'src/components/Admin/AdminTablePlaceholder';
import BadgeActive from 'src/components/Badge/BadgeActive';
import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import LoadMoreButton from 'src/components/LoadMoreButton';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { DEFAULT_LIST_PAGE_SIZE } from 'src/util/constants';
import logger from 'src/util/logger';

const messages = defineMessages({
  paginationError: {
    id: 'admin.admin_meter.admin_meter_list.table.pagination_error.message',
    defaultMessage: 'Failed to load more meters. Please try again.',
  },
});

const AdminMeterListFragment = graphql`
  fragment AdminMeterList_viewer on Viewer
  @throwOnFieldError
  @refetchable(queryName: "AdminMeterListRefetchQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int" }
  )
  {
    id
    meters(
      first: $count
      after: $cursor
    ) @connection(key: "AdminMeterList_meters") {
      edges {
        cursor
        node {
          uuid
          externalIdentifier
          identifier
          active {
            start
            finish
          }
          property {
            uuid
            title
            active {
              start
              finish
            }
          }
          primaryBillingPoint {
            uuid
            identifier
            active {
              start
              finish
            }
          }
        }
      }
    }
  }
`;

/**
 * Skeleton rows rendered behind the pagination error modal, inside the loaded
 * table, to stand in for the page that failed to load.
 * @returns {React.ReactElement} Skeleton table rows.
 */
function PaginationErrorRows() {
  return <AdminTablePlaceholderRows columnCount={6} rowCount={3} />;
}

/**
 * Admin list of meters with load-more pagination. Field errors throw to the
 * route's PlaceholderErrorBoundary; load-more failures show an inline retry modal.
 * @param {object} props - Component props.
 * @param {object|null} props.viewer - Relay fragment reference for the viewer.
 * @returns {React.ReactComponentElement} AdminMeterList component.
 */
function AdminMeterList({ viewer: viewerRef = null }) {
  const {
    data: viewer, loadNext, hasNext, isLoadingNext,
  } = usePaginationFragment(AdminMeterListFragment, viewerRef);
  const [paginationError, setPaginationError] = useState(null);

  const loadMore = () => {
    if (!hasNext || isLoadingNext) {
      return;
    }

    setPaginationError(null);
    loadNext(DEFAULT_LIST_PAGE_SIZE, {
      onComplete: (error) => {
        if (error) {
          logger.error({ error });
          setPaginationError(error);
        }
      },
    });
  };

  const intl = useIntl();

  if (!viewer) {
    // A completed query without a viewer would otherwise never leave the spinner.
    throw new Error('AdminMeterList: the route query returned no viewer');
  }

  const { meters } = viewer;

  // Load more button text
  const copyLoadMore = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.load_more_button.state_load_more.label', defaultMessage: 'Load more...' });
  const copyNoMore = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.load_more_button.state_no_more.label', defaultMessage: 'No more meters to load' });

  // Page title
  const pageTitle = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.page_title', defaultMessage: 'Enosi Admin - Meters' });

  // Breadcrumbs
  const breadcrumbAdmin = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.breadcrumb.admin', defaultMessage: 'Admin' });
  const breadcrumbMeters = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.breadcrumb.meters', defaultMessage: 'Meters' });

  // Page heading
  const headingMeters = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.heading.meters', defaultMessage: 'Meters' });

  // Table headers
  const headerStatus = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.table.header.status', defaultMessage: 'Status' });
  const headerIdentifier = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.table.header.identifier', defaultMessage: 'Identifier' });
  const headerId = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.table.header.id', defaultMessage: 'ID' });
  const headerExternalIdentifier = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.table.header.external_identifier', defaultMessage: 'External identifier' });
  const headerPrimaryBillingPoint = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.table.header.primary_billing_point', defaultMessage: 'Primary Billing Point' });
  const headerProperty = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.table.header.property', defaultMessage: 'Property' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbMeters },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">{headingMeters}</h1>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{headerStatus}</th>
                <th scope="col">{headerIdentifier}</th>
                <th scope="col">{headerId}</th>
                <th scope="col">{headerExternalIdentifier}</th>
                <th scope="col">{headerPrimaryBillingPoint}</th>
                <th scope="col">{headerProperty}</th>
              </tr>
            </thead>
            <tbody>
              {
                // properties && <pre>{JSON.stringify(properties, null, 2)}</pre>
                meters && meters.edges && meters.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    active, externalIdentifier, uuid, identifier, primaryBillingPoint, property,
                  } = node;

                  return (
                    <tr key={`admin-meter-${uuid}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <th scope="row" colSpan={3}>
                        <Link to={`/admin/meters/${uuid}`}>
                          <div>{identifier}</div>
                        </Link>
                        <UUID uuid={uuid} />
                        <br />
                        {`(${externalIdentifier})`}
                      </th>
                      <td>
                        <Link to={`/admin/primary-billing-points/${primaryBillingPoint.uuid}`}>
                          {primaryBillingPoint.identifier}
                        </Link>
                        <br />
                        <UUID uuid={primaryBillingPoint.uuid} />
                        <br />
                        <BadgeActive
                          start={primaryBillingPoint.active.start}
                          finish={primaryBillingPoint.active.finish}
                        />
                      </td>
                      <td>
                        <Link to={`/admin/properties/${property.uuid}`}>
                          {property.title}
                        </Link>
                        <br />
                        <UUID uuid={property.uuid} />
                        <br />
                        <BadgeActive
                          start={property.active.start}
                          finish={property.active.finish}
                        />
                      </td>
                    </tr>
                  );
                })
              }
              {/* The modal renders through a portal; only its skeleton rows render here. */}
              {paginationError && (
                <PlaceholderErrorModal
                  bodyText={intl.formatMessage(messages.paginationError)}
                  error={paginationError}
                  onCancelFunc={() => setPaginationError(null)}
                  onRetryFunc={loadMore}
                  placeholderComponent={PaginationErrorRows}
                  retryDisabled={isLoadingNext}
                />
              )}
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={hasNext}
          />
        </div>
      </div>
    </>
  );
}

AdminMeterList.propTypes = {
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default AdminMeterList;
